/**
 * Fontes de pesquisa web (tool `webSearch`) exibidas abaixo do corpo da
 * mensagem do assistente.
 *
 * - Bloco "Fontes": chips de link com o título da fonte (fallback: hostname).
 *   As URLs são redirects do Vertex (vertexaisearch.cloud.google.com/...).
 * - Widget "Google Search Suggestions" (`searchEntryPointHtml`): exigência de
 *   compliance do Google Grounding — o HTML DEVE ser exibido sem modificação,
 *   por isso vai em um <iframe srcDoc> sandboxed. Com múltiplas buscas no
 *   mesmo turno, apenas o último widget é renderizado (evita duplicação).
 *   ÚNICA alteração permitida no HTML do Google: injetamos
 *   `<meta name="color-scheme" content="light dark">` no <head> (chips, logo,
 *   estilos do `.container` e atribuição ficam byte-a-byte idênticos). Sem esse
 *   opt-in, o documento só estiliza o `.container` e alterna claro/escuro via
 *   `@media (prefers-color-scheme)`, deixando o canvas (html/body ao redor) no
 *   branco padrão do navegador — a "caixa branca" que aparecia no tema escuro.
 *   Com o meta, o canvas passa a acompanhar o mesmo esquema de cor usado pelo
 *   conteúdo do widget, então canvas e chips ficam consistentes (some a caixa).
 *   OBS.: como este <iframe> é sandbox de origem opaca, verifiquei que tanto o
 *   canvas quanto o `prefers-color-scheme` interno seguem o esquema do SO — NÃO
 *   o `color-scheme` do elemento <iframe>. Ou seja, o widget acompanha o tema
 *   do SISTEMA, não o tema do app: se o app estiver escuro mas o SO claro (ou
 *   vice-versa), o widget seguirá o SO. Isso não é contornável por color-scheme
 *   neste iframe sandbox (testado). `style={{ colorScheme }}` é mantido por
 *   consistência/backdrop, mas não força o interior do frame.
 *
 *   COMPLIANCE (pesquisado — não reinvestigar): os Termos do "Grounding with
 *   Google Search" proíbem modificar ou interpor conteúdo no widget ("will not
 *   modify, or intersperse any other content with, the Grounded Results or
 *   Search Suggestions"), por isso o widget é exibido como veio — a única
 *   alteração é o `<meta name="color-scheme">` acima (não reestiliza
 *   chips/logo/atribuição). O Google NÃO expõe parâmetro/API para forçar tema,
 *   cores, largura ou fundo; o único mecanismo de tema sancionado é o próprio
 *   CSS que o Google embute no `renderedContent` (`@media
 *   (prefers-color-scheme: light|dark)`), que se adapta ao esquema de cor do
 *   contexto. Logo, fazer o widget seguir o TEMA DO APP (em vez do tema do SO)
 *   não tem caminho documentado/conforme — exigiria `allow-same-origin` +
 *   reescrever o CSS do Google, violando os Termos e enfraquecendo o sandbox.
 *   Decisão: aceitar a limitação. Fontes:
 *   https://ai.google.dev/gemini-api/docs/grounding/search-suggestions e
 *   https://ai.google.dev/gemini-api/terms (seção Grounding with Google Search).
 */
import type { MessagePart, ToolResult, WebSearchResult } from '@agente/shared'

import { useTheme } from '@/components/theme-provider'

/**
 * Extracts the payload of a successful `webSearch` toolResult part, or null
 * when the part is anything else (other tool, failure, still-running call).
 */
export function webSearchResultData(part: MessagePart): WebSearchResult | null {
  if (part.type !== 'toolResult' || part.toolName !== 'webSearch') return null
  const result = part.toolResult as ToolResult<WebSearchResult> | undefined
  if (result?.ok !== true || !result.data) return null
  return result.data
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

/**
 * Injeta `<meta name="color-scheme" content="light dark">` no <head> do HTML do
 * widget do Google para que o documento opte por `color-scheme`: assim o canvas
 * (fundo html/body ao redor do `.container`) acompanha o mesmo esquema de cor do
 * conteúdo, eliminando a "caixa branca" no tema escuro. Não altera mais nada do
 * HTML: se há `<head>`, insere logo após a abertura; senão, prefixa o meta.
 */
export function withColorSchemeMeta(html: string): string {
  const meta = '<meta name="color-scheme" content="light dark">'
  const headMatch = html.match(/<head[^>]*>/i)
  if (headMatch) {
    const insertAt = (headMatch.index ?? 0) + headMatch[0].length
    return html.slice(0, insertAt) + meta + html.slice(insertAt)
  }
  return meta + html
}

export function SourceLink({ source }: { source: { title?: string; url: string } }) {
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer noopener"
      className="border-border/70 bg-muted/40 text-foreground/80 hover:bg-muted/70 hover:text-foreground inline-flex max-w-72 items-center rounded-md border px-2 py-1 text-xs transition-colors"
    >
      <span className="truncate">{source.title || hostnameOf(source.url)}</span>
    </a>
  )
}

export function SearchSources({ parts }: { parts: MessagePart[] }) {
  const { theme } = useTheme()
  const resolvedTheme =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme

  const results = parts
    .map(webSearchResultData)
    .filter((data): data is WebSearchResult => data !== null)
  if (results.length === 0) return null

  // Dedupe por URL entre múltiplas buscas do mesmo turno; preserva a ordem e
  // aproveita um título vindo de uma busca posterior quando a primeira não tem.
  const sourcesByUrl = new Map<string, { title?: string; url: string }>()
  for (const result of results) {
    for (const source of result.sources) {
      const existing = sourcesByUrl.get(source.url)
      if (!existing) {
        sourcesByUrl.set(source.url, source)
      } else if (!existing.title && source.title) {
        sourcesByUrl.set(source.url, { ...existing, title: source.title })
      }
    }
  }
  const sources = [...sourcesByUrl.values()]

  const entryPointHtml = results
    .map((result) => result.searchEntryPointHtml)
    .filter((html): html is string => Boolean(html))
    .at(-1)

  if (sources.length === 0 && !entryPointHtml) return null

  return (
    <div className="space-y-2">
      {sources.length > 0 ? (
        <div>
          <p className="text-muted-foreground text-xs font-medium">Fontes</p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {sources.map((source) => (
              <li key={source.url}>
                <SourceLink source={source} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {entryPointHtml ? (
        <iframe
          srcDoc={withColorSchemeMeta(entryPointHtml)}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          title="Sugestões de busca do Google"
          className="h-16 w-full rounded-md border-0"
          style={{ colorScheme: resolvedTheme }}
        />
      ) : null}
    </div>
  )
}
